import * as React from 'react';
import { Button, Checkbox, Collapse, InputNumber, Space, Typography } from 'antd';
import { isValidCoordinates, minimumAccuracy, TWPositionData, watchHistory } from '../../Service/GeolocationService';
import UTCDateFormatter from '../../Helper/DateFormatter/UTCDateFomatter';

const { Panel } = Collapse;
const { Text } = Typography;

interface DebugDataProps{
  data?: any;
  coordinates?: any;
  onAccuracyUpdate?: Function;
}

interface DebugDataState{
  accuracy?: {
    enabled?: boolean;
    value?: number|null|undefined;
  }
  availableStorageGb?: number|null;
  persistentStorage?: boolean|null;
}

export default class DebugData extends React.Component<DebugDataProps, DebugDataState>{
  constructor(props: DebugDataProps) {
    super(props);
    this.state = {
      accuracy: {
        enabled: false,
        value: undefined,
      }
    }
  }

  componentDidMount() {
    this.refreshAvailableStorage();
    this.refreshPersistentStorage();
  }

  // The space the app may still fill, in GB, as the browser reports it: `quota` is the share of the device
  // this origin is allowed to use - the storage of photos in the queue is what fills it - and `usage`
  // what is already taken. A page is not given the device's free memory on iOS or Android, so this is
  // the figure that has a meaning here. `estimate()` answers asynchronously and is a snapshot, so it is
  // read once, when this panel is mounted with the form, and the value kept in the state.
  async refreshAvailableStorage() {
    if (!navigator.storage?.estimate) {
      this.setState({ availableStorageGb: null });
      return;
    }

    try {
      const { quota, usage } = await navigator.storage.estimate();
      this.setState({ availableStorageGb: ((quota || 0) - (usage || 0)) / (1024 * 1024 * 1024) });
    } catch (error) {
      console.warn('Could not read the storage estimate:', error);
      this.setState({ availableStorageGb: null });
    }
  }

  // Whether the browser has put this site in its "persistent" bucket, that is whether it promised not to
  // evict the stored trees to reclaim space when the device runs short - the queue is evicted without
  // warning otherwise, even below the quota. `persisted()` is not implemented by every browser (Safari
  // has no `persist()` either), hence the `null` shown as "unavailable" rather than as "not granted".
  async refreshPersistentStorage() {
    if (!navigator.storage?.persisted) {
      this.setState({ persistentStorage: null });
      return;
    }

    try {
      this.setState({ persistentStorage: await navigator.storage.persisted() });
    } catch (error) {
      console.warn('Could not read the persistent storage state:', error);
      this.setState({ persistentStorage: null });
    }
  }

  // Asking again from a click, because that is the only way the answer can become "granted" in most
  // browsers: Firefox shows its permission doorhanger only for a call made during a user gesture, and
  // Chromium decides silently through its own heuristics - an installed web app, a bookmark, an
  // engagement score - so the call made at start up in `src/index.tsx` is refused with no prompt and no
  // explanation. The line above is read again afterwards, so the answer is visible where it was asked.
  async requestPersistentStorage() {
    if (!navigator.storage?.persist) return;

    try {
      const granted = await navigator.storage.persist();
      console.debug('TreeWallet: persistent storage request answered', granted ? 'granted' : 'not granted');
    } catch (error) {
      console.warn('Could not request persistent storage:', error);
    }

    this.refreshPersistentStorage();
  }

  // One block per position, newest first: a header naming it - "[Newest position]" for the first one,
  // then "[2]", "[3]" ... up to "[N]" - its UTC time, then the values as delivered, except the
  // accuracy, which is shown with 2 decimals to stay readable next to its unit.
  // The label is the rank in the list, not the age of the fix: the history receives the same cached fix
  // more than once (the one second polling asks with `maximumAge`), and two fixes of the same second
  // used to be labelled "[Newest position]" twice. A fix which is 10 seconds old or more - where the
  // 10-slot history (`watchHistory.maxValues`) would have dropped it - is not shown, so at most 10
  // blocks are listed.
  renderDebugAllDataItems(debugData: TWPositionData[]) {
    if (debugData.length === 0) {
      return (
        <div>
          <Text>No data yet</Text>
        </div>
      );
    }

    const headerStyle = { marginRight: '8px' };
    const valueStyle = { marginLeft: '5px', marginRight: '12px' };
    const newestTimestamp = debugData[0].timestamp || 0;

    // Only the positions of the last 10 seconds are displayed: the older ones are the ones the 10-slot
    // history is about to drop.
    const positions = debugData.filter((coords: TWPositionData) => {
      const secondsAgo = Math.round((newestTimestamp - (coords.timestamp || newestTimestamp)) / 1000);
      return secondsAgo < watchHistory.maxValues;
    });

    return positions.map((coords: TWPositionData, index: number) => {
      const time = coords.timestamp ? UTCDateFormatter.formatDateFromTimestamp(coords.timestamp).time : '';
      // The raw accuracy carries up to ~15 digits, while 2 are enough to compare the last positions.
      const accuracy = coords.accuracy || 0;
      const accuracyText = `${accuracy.toFixed(2)}m`;

      return (
        <div key={`debugdata-${coords.timestamp}`} style={{ marginBottom: '8px' }}>
          <div>
            <Text mark style={headerStyle}>{index === 0 ? '[Newest position]' : `[${index + 1}]`}</Text>
            <Text mark>{time} (UTC)</Text>
          </div>
          {isValidCoordinates(coords) ? (
            <div>
              <Text strong>Acc</Text><Text style={valueStyle}>{accuracyText}</Text>
              <Text strong>Lat</Text><Text style={valueStyle}>{coords.lat}</Text>
              <Text strong>Lng</Text><Text style={valueStyle}>{coords.lng}</Text>
            </div>
          ) : (
            <Text type="danger">Invalid coordinates</Text>
          )}
        </div>
      );
    })
  }

  // `undefined` while the first estimate is in flight, `null` when the browser does not report one.
  renderAvailableStorage() {
    const { availableStorageGb } = this.state;

    if (availableStorageGb === undefined) {
      return <Text>...</Text>;
    }

    if (availableStorageGb === null) {
      return <Text>unavailable</Text>;
    }

    return <Text>{`${availableStorageGb.toFixed(2)} GB`}</Text>;
  }

  renderPersistentStorage() {
    const { persistentStorage } = this.state;

    if (persistentStorage === undefined) {
      return <Text>...</Text>;
    }

    if (persistentStorage === null) {
      return <Text>unavailable</Text>;
    }

    return <Text>{persistentStorage ? 'granted' : 'not granted'}</Text>;
  }

  renderControls() {
    const { onAccuracyUpdate } = this.props;
    const { accuracy } = this.state;

    const updateAccuracy = (updatedAccuracy: number|null|undefined, enabled: boolean) => {
      this.setState({
        accuracy: { enabled, value: updatedAccuracy }
      });
      onAccuracyUpdate && onAccuracyUpdate(updatedAccuracy);
    };

    return (
      <div>
        <Space size={"middle"}>
          <InputNumber min={1} max={9999} defaultValue={minimumAccuracy} onChange={(e) => updateAccuracy(
            e ? e : 0,
            accuracy?.enabled ? true : false
          )}/>
        </Space>
        <Space>
          <Checkbox
            onChange={(e) => updateAccuracy(
              accuracy?.value,
              e.target.checked
            )}
            defaultChecked={accuracy?.enabled}
          >
          </Checkbox>
        </Space>
      </div>
    );
  }

  render() {
    // A copy, newest first. `watchHistory.values` is shared with the service, and `add` keeps the last
    // `maxValues` *by position*: sorting it in place would put the newest first, so the next fix -
    // pushed at the end - would make it evict the newest instead of the oldest. It would also reorder
    // the `gpsHistory` the tree payload is built from.
    // A position without `timestamp` (an error entry) is sorted as the oldest, so the first block of
    // the list is always the newest fix.
    const data: TWPositionData[] = [...watchHistory.values]
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return (
      <Collapse ghost>
        <Panel header={<Text code>debug gnss ({data.length})</Text>} key={1}>
          {/* The coordinates rows are adjacent inline elements without spaces,
              so they need to be breakable to stay readable on narrow screens. */}
          <div style={{ overflowWrap: 'anywhere' }}>
            <div style={{ marginBottom: '10px', display: 'none' }}>{this.renderControls()}</div>
            <div>
              <div>
                <Text strong>Memory space available = </Text>
                {this.renderAvailableStorage()}
                <Button
                  size="small"
                  style={{ marginLeft: '8px' }}
                  disabled={this.state.availableStorageGb === null}
                  onClick={() => this.refreshAvailableStorage()}
                >
                  Refresh
                </Button>
              </div>
              <div>
                <Text strong>Persistent storage = </Text>
                {this.renderPersistentStorage()}
                <Button
                  size="small"
                  style={{ marginLeft: '8px' }}
                  disabled={this.state.persistentStorage === null}
                  onClick={() => this.requestPersistentStorage()}
                >
                  Request
                </Button>
              </div>
              <div><Text strong>Last 10 positions</Text></div>
              {this.renderDebugAllDataItems(data)}
            </div>
          </div>
        </Panel>
      </Collapse>
    );
  }
}
